// ── Inbox — Group chat handlers (rename, members, pin, open) ──
//
// All 6 group-chat handlers: renaming the group, managing members,
// pinning messages, scheduling group messages, opening a group
// conversation (the largest function — sets active state, renders the
// header with action buttons, fetches messages + members, starts the
// group poll), and deleting a group conversation.
//
// All 6 methods must be re-pinned to `window.*` because the
// conversation-list HTML, chat header HTML, and pinned-message strip
// all emit inline onclick handlers that resolve by global lookup.
//
// `_renderGroupMessages` (formerly an internal helper that just called
// `renderChatMessages`) is folded into the pin handler since it had no
// independent behavior.
//
// State the factory reads (via getters) or mutates (via setters):
//   - read: the conversation list (for find), _activeGroupId / _activeGroupMessages (for pin)
//   - mutate (via setters): _activeGroupId, _activeClientId, _activeConvType,
//     _activeGroupMembers, _activeMessages
//   - the conversation list's writes (unread count, a deleted group) go
//     through the shared store, messages/conversation-store.js
//
// All setters are explicit callbacks because these are `let`-bound and
// reassigned (cannot mutate the binding from the outside without one).

import { getSupabase } from '../config.js';
import { showConfirmDialog } from '../modals.js';
import { showToast } from '../utils.js';
import { escHtml } from './format-helpers.js';
import * as convStore from '../messages/conversation-store.js';
import { getDraft, clearDraft } from '../messages/drafts.js';

/**
 * @param {object} deps
 * @param {() => any[]} deps.getConversations — the shared store's list
 * @param {() => void} deps.applyFilter — rebuilds the visible list from the open tab
 * @param {() => string | null} deps.getActiveGroupId
 * @param {() => any[]} deps.getActiveMessages — also serves as the
 *   group-messages array reference (group mode aliases _activeGroupMessages
 *   to _activeMessages in the parent)
 * @param {(id: string | null) => void} deps.setActiveGroupId
 * @param {(id: string | null) => void} deps.setActiveClientId
 * @param {(t: 'direct' | 'group' | null) => void} deps.setActiveConvType
 * @param {(members: any[]) => void} deps.setActiveGroupMembers
 * @param {(msgs: any[]) => void} deps.setActiveMessages
 * @param {(clientId: string) => void} deps.unsubscribeActiveClientTyping
 * @param {(groupId: string) => void} deps.startGroupMessagePoll
 * @param {() => void} deps.renderConversationsList
 * @param {() => void} deps.renderChatMessages
 * @param {(key: string) => void} [deps.loadComposerDraft] — puts a conversation's draft in the box
 * @param {() => string} [deps.popOutButtonHtml] — the header's Pop out button (inbox.js)
 */
export function createInboxGroup(deps) {
  const {
    getConversations,
    applyFilter,
    getActiveGroupId,
    getActiveMessages,
    setActiveGroupId,
    setActiveClientId,
    setActiveConvType,
    setActiveGroupMembers,
    setActiveMessages,
    unsubscribeActiveClientTyping,
    startGroupMessagePoll,
    subscribeGroupChannel,
    renderConversationsList,
    renderChatMessages,
    loadComposerDraft,
    popOutButtonHtml,
  } = deps;

  function editGroupName(groupId) {
    const label = document.getElementById('inbox-group-name-label');
    if (!label) return;
    const current = label.textContent.trim();
    const input = document.createElement('input');
    input.type = 'text';
    input.value = current;
    input.className = 'inbox-group-name-edit';
    input.style.cssText = 'font-size:16px;font-weight:700;background:var(--surface);border:1px solid var(--primary);border-radius:6px;padding:4px 10px;outline:none;color:var(--text);width:200px;';

    const save = async () => {
      const newName = input.value.trim();
      if (newName && newName !== current) {
        const supabase = getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        await fetch('/.netlify/functions/messages-group-update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ groupId, name: newName }),
        });
        const conv = getConversations().find(c => c.groupId === groupId);
        if (conv) conv.groupName = newName;
        showToast('Group renamed', 'success');
      }
      label.textContent = input.value.trim() || current;
      label.style.display = '';
      input.remove();
      renderConversationsList();
    };

    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { label.style.display = ''; input.remove(); } });
    input.addEventListener('blur', save);
    label.style.display = 'none';
    label.parentElement.insertBefore(input, label.nextSibling);
    input.focus();
    input.select();
  }

  async function openGroupMembers(groupId) {
    const existing = document.getElementById('group-members-modal');
    if (existing) existing.remove();

    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();

    // Fetch current members
    const { data: members } = await supabase
      .from('message_group_members')
      .select('client_id')
      .eq('group_id', groupId);
    const memberIds = new Set((members || []).map(m => m.client_id));

    // Fetch all coach's clients
    const { data: allClients } = await supabase
      .from('clients')
      .select('id, data')
      .eq('coach_id', session.user.id);

    const clientList = (allClients || []).map(c => ({
      id: c.id,
      name: [c.data?.firstName, c.data?.lastName].filter(Boolean).join(' ') || c.data?.email || 'Client',
      isMember: memberIds.has(c.id),
    })).sort((a, b) => (b.isMember ? 1 : 0) - (a.isMember ? 1 : 0) || a.name.localeCompare(b.name));

    const overlay = document.createElement('div');
    overlay.id = 'group-members-modal';
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal-box" style="max-width:420px;">
        <div class="modal-header"><h3>Manage Members</h3><button class="modal-close" onclick="document.getElementById('group-members-modal').remove()">&times;</button></div>
        <div class="modal-body" style="max-height:400px;overflow-y:auto;">
          ${clientList.map(c => `
            <label class="inbox-member-row" style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border);cursor:pointer;">
              <input type="checkbox" class="inbox-member-check" data-client-id="${c.id}" ${c.isMember ? 'checked' : ''} style="width:18px;height:18px;accent-color:var(--primary);" />
              <span style="font-size:14px;font-weight:500;color:var(--text);">${escHtml(c.name)}</span>
            </label>`).join('')}
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" onclick="document.getElementById('group-members-modal').remove()">Cancel</button>
          <button class="btn-primary" id="group-members-save-btn">Save Changes</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

    document.getElementById('group-members-save-btn').addEventListener('click', async () => {
      const checks = overlay.querySelectorAll('.inbox-member-check');
      const addIds = [], removeIds = [];
      checks.forEach(cb => {
        const cid = cb.dataset.clientId;
        if (cb.checked && !memberIds.has(cid)) addIds.push(cid);
        if (!cb.checked && memberIds.has(cid)) removeIds.push(cid);
      });

      if (addIds.length === 0 && removeIds.length === 0) { overlay.remove(); return; }

      const btn = document.getElementById('group-members-save-btn');
      btn.disabled = true; btn.textContent = 'Saving...';

      const res = await fetch('/.netlify/functions/messages-group-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ groupId, addClientIds: addIds.length ? addIds : undefined, removeClientIds: removeIds.length ? removeIds : undefined }),
      });
      const json = await res.json();
      overlay.remove();

      if (json.ok) {
        const conv = getConversations().find(c => c.groupId === groupId);
        if (conv) conv.memberCount = json.memberCount;
        const badge = document.querySelector('.inbox-group-member-badge');
        if (badge) badge.textContent = json.memberCount + ' members';
        renderConversationsList();
        showToast(json.changes.join(', '), 'success');
      }
    });
  }

  async function pinMessage(messageId, currentlyPinned) {
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();

    const res = await fetch('/.netlify/functions/messages-pin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ messageId, pinned: !currentlyPinned }),
    });
    const json = await res.json();
    if (json.ok) {
      // Update local message — in group mode, _activeGroupMessages aliases _activeMessages
      const msg = getActiveMessages()?.find(m => m.id === messageId);
      if (msg) msg.pinned_at = json.pinned ? new Date().toISOString() : null;
      // Re-render group chat
      if (getActiveGroupId()) renderChatMessages();
      showToast(json.pinned ? 'Message pinned' : 'Message unpinned', 'success');
    }
  }

  async function scheduleGroupMessage(groupId) {
    const body = document.getElementById('uc-textarea')?.value?.trim();
    if (!body) { showToast('Type a message first', 'error'); return; }

    const scheduleTime = prompt('Schedule for (e.g. "2026-04-13T09:00"):');
    if (!scheduleTime) return;

    const scheduledFor = new Date(scheduleTime);
    if (isNaN(scheduledFor.getTime()) || scheduledFor <= new Date()) {
      showToast('Enter a valid future date/time', 'error');
      return;
    }

    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();

    const res = await fetch('/.netlify/functions/scheduled-email-create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ groupId, body, scheduledFor: scheduledFor.toISOString(), channel: 'in_app' }),
    });
    const json = await res.json();
    if (json.ok) {
      document.getElementById('uc-textarea').value = '';
      // The draft goes with the schedule (see inbox.js _showScheduleMessage), unless it holds newer words.
      if (getDraft(`g:${groupId}`).trim() === body) clearDraft(`g:${groupId}`);
      showToast('Message scheduled for ' + scheduledFor.toLocaleString(), 'success');
    }
  }

  // Resolves true once this group's messages are on screen, false if they
  // failed to load or another chat was opened while they loaded.
  async function openInboxGroupConversation(groupId) {
    setActiveGroupId(groupId);
    setActiveClientId(null);
    setActiveConvType('group');
    setActiveGroupMembers([]);
    // ⚠️ The last chat's messages go now, not when this group's arrive. Left in
    // memory, a load that failed kept them there, and the next redraw drew
    // them under this group's name, with Reply buttons, while the box sent to
    // the group.
    setActiveMessages([]);
    unsubscribeActiveClientTyping(); // typing is 1:1 only for now
    // ⚠️ THE BOX HOLDS THIS GROUP'S OWN DRAFT (messages/drafts.js). It used
    // to keep whatever was typed in the chat open before, and Send gave a
    // client's words to the whole group.
    loadComposerDraft?.(`g:${groupId}`);

    const conv = getConversations().find(c => c.type === 'group' && c.groupId === groupId);
    const groupName = conv?.groupName || 'Group Chat';

    // Show chat pane
    const listPane = document.getElementById('inbox-list-pane');
    const chatPane = document.getElementById('inbox-chat-pane');
    listPane?.classList.add('conv-open');
    chatPane?.classList.remove('hidden');

    // Header with group name + member count + actions
    const header = document.getElementById('inbox-chat-header');
    const isStarred = conv?.starred || false;
    const isArchived = conv?.archived || false;
    header.innerHTML = `
      <button class="inbox-chat-back" onclick="window.closeInboxConversation()">&lsaquo; Back</button>
      <span class="inbox-chat-name" id="inbox-group-name-label" ondblclick="window._editGroupName('${groupId}')">${escHtml(groupName)}</span>
      ${conv?.memberCount ? `<span class="inbox-group-member-badge" style="cursor:pointer;" onclick="window._openGroupMembers('${groupId}')">${conv.memberCount} members</span>` : ''}
      <div class="inbox-header-actions" id="inbox-header-actions">
        ${popOutButtonHtml?.() || ''}
        <button class="inbox-ha-btn" onclick="window._editGroupName('${groupId}')" title="Rename group">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button class="inbox-ha-btn" onclick="window._openGroupMembers('${groupId}')" title="Manage members">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </button>
        <button class="inbox-ha-btn${isStarred ? ' active' : ''}" data-ha="star" onclick="window._starConversation()" title="${isStarred ? 'Unstar' : 'Star'}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="${isStarred ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </button>
        <button class="inbox-ha-btn" onclick="window._archiveConversation()" title="${isArchived ? 'Unarchive' : 'Archive'}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        </button>
        <button class="inbox-ha-btn" onclick="window._markConversationUnread()" title="Mark as unread">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
        </button>
        <button class="inbox-ha-btn inbox-ha-btn--danger" onclick="window._deleteConversation()" title="Delete conversation">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
        </button>
      </div>`;

    // Loading state
    const messagesEl = document.getElementById('inbox-chat-messages');
    messagesEl.innerHTML = '<div style="padding:40px;text-align:center;color:var(--text-muted);">Loading…</div>';

    renderConversationsList();

    // Fetch group messages
    let loaded = true;
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/messages-group-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ groupId }),
      });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      // Another chat was opened while this loaded: its screen is not ours.
      if (getActiveGroupId() !== groupId) return false;
      setActiveMessages(json.messages || []);
      setActiveGroupMembers(json.members || []); // now includes lastReadAt per member
      renderChatMessages();

      // Mark as read. Every number drops at once, and the shared store tells
      // the server. ⚠️ It also clears "Mark as unread": opening a group never
      // did, so a group marked unread stayed counted after it was read.
      await convStore.markRead(`g:${groupId}`);
      renderConversationsList();
    } catch {
      if (getActiveGroupId() !== groupId) return false;
      messagesEl.innerHTML = '<div style="padding:40px;text-align:center;color:var(--text-muted);">Failed to load messages.</div>';
      loaded = false;
    }
    // ⚠️ Left (another chat, another page) while the read mark was on its way:
    // no poll and no live channel for a group the Inbox no longer shows. Held
    // here, the channel stayed open until the Inbox next opened or closed a chat.
    if (getActiveGroupId() !== groupId) return false;

    document.getElementById('uc-textarea')?.focus();
    startGroupMessagePoll(groupId);
    // Live group reactions while this conversation is open (messages
    // already arrive via the global INSERT listener).
    subscribeGroupChannel(groupId);
    return loaded;
  }

  function deleteGroupConversation(groupId) {
    showConfirmDialog({
      message: 'Delete this group conversation for everyone? All messages will be permanently removed.',
      okLabel: 'Delete Group',
      okClass: 'btn-danger',
      onConfirm: async () => {
        try {
          const supabase = getSupabase();
          const { data: { session } } = await supabase.auth.getSession();
          const res = await fetch('/.netlify/functions/messages-group-delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ groupId }),
          });
          if (!res.ok) throw new Error('Failed');
          convStore.remove(`g:${groupId}`);
          // Through the open tab: a copy of every loaded chat put archived ones under All.
          applyFilter();
          window.closeInboxConversation();
          renderConversationsList();
          convStore.notify();
        } catch {}
      },
    });
  }

  return {
    editGroupName,
    openGroupMembers,
    pinMessage,
    scheduleGroupMessage,
    openInboxGroupConversation,
    deleteGroupConversation,
  };
}
